import {useEffect, useMemo, useState} from "react";
import Swal from "sweetalert2";

import {httpRequest} from "~/fetch/common/http-request";

import {
    MachineBoardExcelRequest,
    MachineBoardRefinedData,
    MachineBoardRequest,
    MachineBoardResponse,
    MachineChartRefinedData,
    MachineChartResponse,
} from "./use-machine.interface";

const useMachineBoardList = (config: MachineBoardRequest) => {
    const [response, setResponse] = useState<MachineBoardRefinedData | null>(null);
    const [error, setError] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const request = useMemo(() => {
        const {
            pageNo,
            isNoImage,
            searchMachineBuildingId,
            searchMachineCategoryCode,
            searchMachineSectorId,
            searchMachineUseYn,
            searchProcessCode,
            trainerSectorId,
        } = config;
        return httpRequest<MachineBoardRequest, MachineBoardResponse>({
            method: "GET",
            url: "/machine/ml",
            params: {
                pageNo,
                isNoImage,
                searchMachineBuildingId,
                searchMachineCategoryCode,
                searchMachineSectorId,
                searchMachineUseYn,
                searchProcessCode,
                trainerSectorId,
            },
        });
    }, [config]);

    const submit = async () => {
        try {
            setIsLoading(true);
            const response = await request.submit();

            const data = response.data.machineCategoryDTOList.map((data: any) => ({
                machineId: data.machineId,
                buildingName: data.buildingName,
                machineName: data.machineName,
                machineCategoryName: data.machineCategoryName,
                machineInstallCompany: data.machineInstallCompany,
                machineInstallDate: data.machineInstallDate,
                machineManufactureCompany: data.machineManufactureCompany,
                machineUseYn: data.machineUseYn,
                process: data.process,
                updateDate: data.updateDate,
                machineCategoryCode: data.machineCategoryCode,
                buildingId: data.buildingId,
                companyId: data.companyId,
                capacityValue1: data.capacityValue1,
                capacityValue2: data.capacityValue2,
                capacityUnit1: data.capacityUnit1,
                capacityUnit2: data.capacityUnit2,
            }));

            setResponse({
                resultList: data,
                allRecordCnt: response.data.machineSearchVO.maxCount,
            });
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const listExcel = async () => {
        if (config.searchMachineCategoryCode === "ALL") {
            Swal.fire({
                text: "카테고리 조회 후 엑셀 다운로드 가능합니다.",
                confirmButtonText: "확인",
            });
        } else {
            return await httpRequest<MachineBoardExcelRequest, Blob>({
                method: "GET",
                url: "/machine/excel",
                params: {
                    ...config,
                },
                responseType: "blob",
            })
                .submit()
                .then((res) => {
                    // 파일 다운로드 처리
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    // 파일명추출
                    const contentType = res.headers["content-type"];
                    let fileName = "downloaded_file";
                    if (contentType) {
                        const matches = /filename=([^;]*)/.exec(contentType);
                        if (matches != null && matches[1]) {
                            fileName = matches[1].trim().replace(/['"]/g, "");
                        }
                    }

                    link.setAttribute("download", decodeURIComponent(fileName.replace(/\+/g, " ")));
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                })
                .catch((e) => {
                    Swal.fire({
                        text: "오류가 발생하였습니다. 다시 시도해주세요",
                        confirmButtonText: "확인",
                    });
                });
        }
    };

    return [submit, response, listExcel, isLoading, error];
};

const useMachineBoardChart = () => {
    // MachineChartRefinedData
    const [chartResponse, setChartResponse] = useState<MachineChartRefinedData | any>({
        sector1: 0,
        sector2: 0,
        sector3: 0,
        sector4: 0,
        sector5: 0,
        sector6: 0,
        electricity: 0,
        firefighting: 0,
        building: 0,
        autoSorter: 0,
        elevator: 0,
        freezing: 0,
        machine: 0,
        useY: 0,
        useN: 0,
    });
    // const [chartError, setChartError] = useState<any>(null);
    const [isLoadingChart, setIsLoadingChart] = useState<boolean>(false);

    useEffect(() => {
        setIsLoadingChart(true);
        const requestMachine = httpRequest<null, MachineChartResponse>({
            method: "GET",
            url: "/machine/ml/chart",
            params: {
                isNoImage: "ALL",
                searchMachineCategoryCode: "ALL",
                searchMachineUseYn: "ALL",
            },
        });

        requestMachine
            .submit()
            .then((res) => {
                setChartResponse(res.data);
            })
            .finally(() => {
                setIsLoadingChart(false);
            });
    }, []);

    return [chartResponse, isLoadingChart];
};

// mobile - 설비기사 로그인시 config 필요없음
const useMobileMachineBoardList = (config: any) => {
    const [response, setResponse] = useState<MachineBoardRefinedData | null>(null);
    const [error, setError] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const request = useMemo(() => {
        return httpRequest<null, MachineBoardResponse>({
            method: "GET",
            url: "/machine/ml",
            params: config,
        });
    }, []);

    const submit = async () => {
        try {
            setIsLoading(true);
            const response = await request.submit();

            const data = response.data.machineCategoryDTOList.map((data: any) => ({
                machineId: data.machineId,
                buildingName: data.buildingName,
                machineName: data.machineName,
                machineCategoryName: data.machineCategoryName,
                machineInstallCompany: data.machineInstallCompany,
                machineInstallDate: data.machineInstallDate,
                machineManufactureCompany: data.machineManufactureCompany,
                machineUseYn: data.machineUseYn,
                process: data.process,
                updateDate: data.updateDate,
                machineCategoryCode: data.machineCategoryCode,
                buildingId: data.buildingId,
                companyId: data.companyId,
                capacityValue1: data.capacityValue1,
                capacityValue2: data.capacityValue2,
                capacityUnit1: data.capacityUnit1,
                capacityUnit2: data.capacityUnit2,
            }));

            setResponse({
                resultList: data,
                allRecordCnt: response.data.machineSearchVO.maxCount,
            });
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    return [submit, response, isLoading, error];
};

// mobile - 설비기사 로그인시 설비코드 가져오기
const useMobileProcessData = () => {
    const [response, setResponse] = useState<{code: string; name: string}[]>([{code: "", name: ""}]);
    // const [error, setError] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        const requestMachine = httpRequest({
            url: "/common/detailCodes?groupCd=LC007",
        });

        requestMachine
            .submit()
            .then((res: any) => {
                setIsLoading(true);
                const getProcessData = res.data.map((i: {code: string; description: string}) => ({
                    code: i.code,
                    name: i.description,
                }));
                setResponse(getProcessData);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    return {response, isLoading};
};

export {useMachineBoardChart, useMachineBoardList, useMobileMachineBoardList, useMobileProcessData};
